/**
 * The organisation's identity, as the CMS holds it.
 *
 * This module is the snapshot that seeds OrganizationSettings. After the first
 * seed the database is the source of truth and staff edit it in the admin
 * panel; this file remains the record of where each value came from, which is
 * the whole reason the project keeps a committed snapshot at all.
 *
 * FACT POLICY
 * -----------
 * `confirmed: true` means the value is published by HISCUS on its own website
 * or profiles, and the source is named in the comment beside it. `confirmed:
 * false` means it is not published, or sources conflict: the site renders a
 * neutral fallback and the item is listed in CONTENT_CONFIRMATION.md for client
 * sign-off. Never promote a value without a client-approved source.
 */

export type ConfirmableValue = { value: string; confirmed: boolean; note?: string };

export const organisationSettings = {
  // Source: https://himshikharanepal.org.np/homepage/about-us/
  legalName: "Himshikhara Socio-Cultural Society",
  shortName: "HISCUS Nepal",
  initials: "HISCUS",
  establishedYear: 1998,
  /**
   * Deliberately empty. HISCUS publishes the year, not the day, and a counter
   * of "calendar years since 1998" was removed from the homepage for exactly
   * this reason. Fill it only from a registration certificate.
   */
  foundingDate: "",

  positioning: "Community-led change in Nepal, building dignity, opportunity and resilience since 1998.",
  motto: "Support. Strengthen. Sustain.",
  tagline: "Serving communities since 1998",

  // Source: official About page.
  vision: "A prosperous and equitable Nepal where every community thrives in dignity and sustainability.",
  mission:
    "To improve the lives of women, children and marginalised Nepalese communities through education, empowerment, poverty reduction and community development.",

  /**
   * Working principles.
   *
   * Each one describes something the documented record shows HISCUS doing, not
   * an aspiration: sessions requested by wards, figures published with their
   * period, relief that leaves a tank or a path behind.
   */
  values: [
    {
      title: "Community-led",
      body: "Programmes begin with a request from a ward, school, health post or community group, and are planned with the people who will host them.",
    },
    {
      title: "Evidence-based",
      body: "What HISCUS publishes is limited to what it can evidence, with the period each figure refers to.",
    },
    {
      title: "Dignity first",
      body: "Participants are described as participants, never as cases or objects of pity. No personal history is published without consent.",
    },
    {
      title: "Durable",
      body: "Relief work is expected to leave something behind: a water tank, an access path, a protective wall.",
    },
  ],

  logoMediaKey: "brand/hiscus-logo",
  logoDarkMediaKey: "brand/hiscus-logo-dark",
  logoReverseMediaKey: "brand/hiscus-logo-reverse",
  faviconMediaKey: "",
  socialImageUrl: "/og-default.jpg",

  /**
   * Theme.
   *
   * The navy is sampled from the emblem in the supplied logo file and is the
   * same value the design tokens carry. It is here so the platform can be
   * rebranded for another organisation without a code change, not so that this
   * one gets recoloured.
   */
  theme: {
    primary: "#14386f",
    secondary: "#2f6f9f",
    accent: "#b9ddeb",
    headingFont: "source-serif-4" as const,
    bodyFont: "manrope" as const,
    density: "comfortable" as const,
  },

  /**
   * Contact.
   *
   * All three were published on HISCUS's own website and are now confirmed. The
   * earlier build showed "Kathmandu, Nepal" and no phone or email because the
   * conflict between Panipokhari-3, Panipokhari-9 and Narayan Gopal Sadak had
   * not been resolved; the organisation's own About and Contact pages both give
   * Panipokhari-3, Lazimpat, so that is what is published.
   */
  // Source: https://himshikharanepal.org.np/homepage/contact-us/ and /homepage/about-us/
  address: {
    value: "Panipokhari-3, Lazimpat, Kathmandu, Nepal",
    confirmed: true,
    note: "Published on the organisation's own Contact and About pages. A map pin and ward-level confirmation are still wanted.",
  } satisfies ConfirmableValue,
  addressLocality: "Kathmandu",
  addressRegion: "Bagmati",
  postalCode: "",
  addressCountry: "NP",

  /**
   * Published exactly as HISCUS publishes it.
   *
   * The organisation's site writes "+9774515393", which is the Kathmandu
   * landline 4515393 behind the country code with the area code elided. It is
   * reproduced verbatim rather than reformatted into +977 1 4515393, because
   * inserting a digit group nobody published is a small invention and this
   * project does not make those.
   */
  // Source: https://himshikharanepal.org.np/homepage/contact-us/
  phone: {
    value: "+9774515393",
    confirmed: true,
    note: "As published. Ask the client whether the dialling format should be +977 1 4515393, and whether a WhatsApp number should be shown.",
  } satisfies ConfirmableValue,

  // Source: https://himshikharanepal.org.np/homepage/contact-us/
  email: {
    value: "himshikhara1998@gmail.com",
    confirmed: true,
    note: "The published enquiry address. A domain address would read better to institutional funders.",
  } satisfies ConfirmableValue,

  mapEmbedUrl: {
    value: "",
    confirmed: false,
    note: "Add an embedded map only once the exact office location is confirmed by the client.",
  } satisfies ConfirmableValue,

  /**
   * Safeguarding.
   *
   * Nothing is published. A safeguarding page without a reporting route is of
   * limited use, and a made-up one is worse than none, so the page says the
   * route is being confirmed and the enquiry form is offered instead.
   */
  safeguardingContact: {
    value: "",
    confirmed: false,
    note: "A named person or role, and a direct way to reach them, required from the client.",
  } satisfies ConfirmableValue,

  socialLinks: [
    { label: "Facebook", href: "https://www.facebook.com/hiscus/", external: true, order: 0 },
    {
      label: "LinkedIn",
      href: "https://www.linkedin.com/company/himshikhara-socio-cultural-society1998/",
      external: true,
      order: 1,
    },
    { label: "Instagram", href: "https://www.instagram.com/hiscus1998/", external: true, order: 2 },
    {
      label: "YouTube",
      href: "https://www.youtube.com/@himshikharasocio-cultural5286",
      external: true,
      order: 3,
    },
    { label: "Linktree", href: "https://linktr.ee/hiscus1998", external: true, order: 4 },
  ],

  /**
   * Registration.
   *
   * `verified: false` on all three is not doubt about the numbers: they are
   * published on the organisation's own About page and are shown on the site.
   * It means nobody has checked them against a certificate, and only a checked
   * identifier is emitted in the structured data a search engine reads as a
   * claim about a legal entity. Scans turn these to true.
   */
  // Source: https://himshikharanepal.org.np/homepage/about-us/
  registrations: [
    {
      label: "District Administration Office registration",
      value: "742/054/55",
      issuer: "CDO Office",
      verified: false,
      order: 0,
    },
    {
      label: "Social Welfare Council affiliation",
      value: "7408",
      issuer: "Social Welfare Council",
      verified: false,
      order: 1,
    },
    { label: "PAN", value: "302144668", issuer: "Inland Revenue Department", verified: false, order: 2 },
  ],

  /**
   * Support.
   *
   * There is deliberately no donate button and no DonateAction in the
   * structured data. Both stay off until a verified payment destination,
   * receipting and the legal basis for it exist. A checkout that does not work
   * is worse than none, and a DonateAction pointing at a contact form is a lie
   * told to a search engine.
   */
  support: {
    donationUrl: "",
    donationEnabled: false,
    bankDetailsAvailable: false,
    supportContactRoute: "/get-involved/support",
    note: "Support enquiries open a direct conversation about what you would like to fund and how the transfer would work.",
  },

  /**
   * Charity Trek.
   *
   * The destination is Mountain Routes' own community page, and it is
   * configurable because HISCUS may be given a more exact Charity Trek address
   * later and should not need a deploy to use it. The disclosure line is shown
   * next to the button: the page it opens is not operated by HISCUS, and the
   * site says so rather than letting the visitor assume otherwise.
   */
  charityTrek: {
    partnerName: "Mountain Routes",
    partnerUrl: "https://mountainroutes.com/",
    destinationUrl: "https://mountainroutes.com/community-empowerment",
    ctaLabel: "Explore Charity Trek",
    disclosure:
      "Opens mountainroutes.com in a new tab. Mountain Routes is the trekking partner and operates that website; HISCUS does not.",
  },

  source: "https://himshikharanepal.org.np/ (organisation's own website), September 2026",
  confirmed: true,
};
